import Drift from 'drift-zoom';
import Flickity from 'flickity';

import 'flickity-imagesloaded';

const selectors = {
  productNav: '[data-product-nav]',
  productNavInner: '[data-product-nav-inner]',
  productImage: '[data-product-image]',
  productThumbnail: '[data-product-thumbnail]',
  mainCarousel: '[data-image-carousel]',
  selectedImage: '.is-selected',
  navPrev: '[data-nav-prev]',
  navNext: '[data-nav-next]',
  colorItems: '[data-color]',
  firstActiveImage: '.image--selected',
};

/**
 * Product image gallery on the PDP
 *
 * @export
 * @class ProductImages
 */
export default class ProductImages {
  constructor(productContainer) {
    this.el = productContainer;
    this.slider = productContainer.querySelector(selectors.mainCarousel);
    this.sliderItems = productContainer.querySelectorAll(
      selectors.productImage,
    );
    this.hasVariantGallery = this.el.querySelector(
      selectors.mainCarousel,
    ).dataset.useVariantGallery;

    this.mediaItems = productContainer.querySelectorAll(selectors.colorItems);
    this.pageCount = this.el.querySelector('.slider-counter--current');
    this.pageTotal = this.el.querySelector('.slider-counter--total');

    this.nav = productContainer.querySelector(selectors.productNav);
    this.navInner = this.nav.querySelector(selectors.productNavInner);
    this.navItems = this.nav.querySelectorAll(selectors.productThumbnail);
    this.prevButton = this.el.querySelector(selectors.navPrev);
    this.nextButton = this.el.querySelector(selectors.navNext);
    this.activeSlide = this.el.querySelector('.slider__slide.active');
    this.imageZoomEnabled = this.el.querySelector(
      selectors.mainCarousel,
    ).dataset.imageZoomEnabled;
    if (!this.slider || !this.nextButton) {
      return;
    }

    this._initNav();

    // If variant gallery is active, use css carousel
    // Otherwise, use Flickity to scroll through images
    if (this.hasVariantGallery === 'true') {
      this.initPages();

      // window.addEventListener('variant:update', this.goToFirstSlide.bind(this));
      window.addEventListener('slideChanged', this._scrollNav.bind(this));
      const resizeObserver = new ResizeObserver(entries => this.initPages());
      resizeObserver.observe(this.slider);

      this.slider.addEventListener('scroll', this.update.bind(this));
      this.prevButton.addEventListener('click', this._onNavClick.bind(this));
      this.nextButton.addEventListener('click', this._onNavClick.bind(this));
      this.updateActiveSlide(this.activeSlide);
    } else {
      this._initCarousel();
    }

    this.zoomTrigger = document.querySelector('.zoom-trigger');

    this.zoomTrigger.addEventListener('click', e => {
      e.preventDefault();
      e.target.classList.add('hidden');
      this._toggleZoom();
    });
  }

  /**
   * Initialize primary image carousel
   */
  _initCarousel() {
    const $selectedImage = this.el.querySelector(selectors.firstActiveImage);
    if (!$selectedImage) {
      return;
    }
    // Update model info on initial page load
    this._updateModelInfo($selectedImage.dataset.modelInfoText);

    // Options shared on Desktop and Mobile
    const flickityOptions = {
      pageDots: false,
      cellAlign: 'center',
      imagesLoaded: true,
      percentPosition: true,
      draggable: true,
      prevNextButtons: false,
      lazyLoad: true,
    };

    // If a variant has an image pre-selected, start the slideshow at that image
    // If the slideshow has been re-initalised across a break point, slideshow
    // restarts at same slide
    if ($selectedImage) {
      const initialIndex = parseInt(
        $selectedImage.dataset.productImageIndex,
        10,
      );
      flickityOptions.initialIndex = initialIndex;
    }

    this.flickity = new Flickity(
      this.el.querySelector(selectors.mainCarousel),
      flickityOptions,
    );

    this._bindGallery();
  }

  _bindGallery() {
    const self = this;
    this.flickity.on(
      'staticClick',
      (event, pointer, cellElement, cellIndex) => {
        this._toggleZoom(cellElement);
      },
    );
    this.flickity.on('select', index => {
      const selectedClasses = ['selected', 'thumbnail--selected'];
      const newThumb = this.navItems[index];

      this.navItems.forEach(el => {
        el.classList.remove(...selectedClasses);
      });
      newThumb.classList.add(...selectedClasses);
      document.body.classList.remove('image-zoom--active');
      this.zoomTrigger.classList.remove('hidden');

      if (this.flickity.selectedIndex - 1 < 0) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = false;
      } else if (
        this.flickity.selectedIndex + 1 >=
        this.flickity.slides.length
      ) {
        this.nextButton.disabled = true;
        this.prevButton.disabled = false;
      } else {
        this.prevButton.disabled = false;
        this.nextButton.disabled = false;
      }

      this._scrollNav(newThumb);
    });

    this.flickity.on('change', index => {
      this._updateModelInfo(self.sliderItems[index].dataset.modelInfoText);
    });

    this.flickity.on('lazyLoad', (_, element) => {
      const slideImage = element.querySelector('img');
      slideImage.classList.remove('blur');
    });

    /**
     * Navigate product image gallery with custom buttons
     */

    // Go to previous slide
    this.prevButton.addEventListener('click', e => {
      e.preventDefault();
      this.flickity.previous();
    });

    // Go to next slide
    this.nextButton.addEventListener('click', e => {
      e.preventDefault();
      this.flickity.next();
    });
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      element => element.clientWidth > 0,
    );
    this.sliderLastItem =
      this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
    if (this.sliderItemsToShow.length === 0) {
      return;
    }
    this.slidesPerPage = Math.floor(
      this.slider.clientWidth / this.sliderItemsToShow[0].clientWidth,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll(selectors.productImage);
    this.initPages();
  }

  update() {
    if (!this.pageCount || !this.pageTotal || !this.sliderLastItem) {
      return;
    }
    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderLastItem.clientWidth) + 1;

    if (this.currentPage === 1) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.currentPage === this.totalPages) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    if (this.currentPage != previousPage) {
      window.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }
  }

  _onNavClick(event) {
    event.preventDefault();
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + this.sliderLastItem.clientWidth
        : this.slider.scrollLeft - this.sliderLastItem.clientWidth;

    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  /**
   * Initialize clicking on thumbnail navigation
   */
  _initNav() {
    this.nav.addEventListener('click', event => {
      if (this.hasVariantGallery === 'true') {
        const navItems = Array.prototype.slice.call(this.navInner.children);
        const index = navItems.indexOf(event.target.parentElement);
        const activeMedia = this.sliderItems[index];

        this.slider.scrollTo({
          left: activeMedia.offsetLeft,
        });
      } else {
        const navItems = Array.prototype.slice.call(this.navInner.children);
        const index = navItems.indexOf(event.target.parentElement);
        this.flickity.select(index);
      }
    });
  }
  /**
   * Reset gallery to first image on load / variant change
   */

  goToFirstSlide() {
    if (!this.el.querySelector('.slider__slide.active')) {
      return;
    }

    this.slider.scrollTo({
      left: 0 - this.activeSlide.clientWidth,
    });

    this.updateActiveSlide(this.el.querySelector('.slider__slide.active'));
  }

  /**
   * Add class to div that's clicked/selected
   * @param {Element} element Parent element to grab index
   */
  updateActiveSlide(element) {
    const currentIndex = Array.prototype.slice
      .call(this.slider.children)
      .indexOf(element);
    const currentActiveThumb = this.navItems[currentIndex];

    if (!element && !currentActiveThumb) {
      return;
    }

    const selectedClasses = ['selected'];

    this.navItems.forEach(el => {
      el.classList.remove(...selectedClasses);
    });

    currentActiveThumb.classList.add(...selectedClasses);

    this._resetZoom();

    this.sliderItems.forEach(el => {
      el.classList.remove('is-selected');
    });
    element.classList.add('is-selected');
    const slideImage = element.querySelector('img');
    slideImage.addEventListener(
      'load',
      () => {
        slideImage.classList.remove('blur');
      },
      { once: true },
    );
    slideImage.setAttribute(
      'src',
      slideImage.getAttribute('data-flickity-lazyload'),
    );

    this._updateModelInfo(element.dataset.modelInfoText);
  }

  /**
   * Scrolls nav to show currently selected thumbnail in view
   * @param {Element} newThumb newly selected thumbnail element
   */
  _scrollNav(event) {
    let newThumb = null;
    if (this.hasVariantGallery === 'true') {
      const activeElement = event.detail.currentElement;
      const currentIndex = Array.prototype.slice
        .call(this.slider.children)
        .indexOf(activeElement);
      newThumb = this.navItems[currentIndex];
      if (!activeElement) {
        return;
      }
      this.updateActiveSlide(activeElement);
    } else {
      newThumb = event;
    }

    const navAxis = 'left';

    // Gets the size of the nav element
    const navSize = this.nav.clientWidth;

    // Gets the size of the newly selected thumbnail
    const thumbSize = newThumb.scrollWidth;

    // Gets the offset of the newly selected thumbnail relative to the nav
    const thumbOffset = newThumb.offsetLeft;

    // Scrolls the nav so that the selected thumbnail is centered
    this.nav.scroll({
      // eslint-disable-next-line no-magic-numbers
      [navAxis]: thumbOffset - (navSize - thumbSize) / 2,
      behavior: 'smooth',
    });
  }

  /**
   * Handler for swapping images when the variant changes
   *
   * @param {Object} variant The currently selected variant
   * @public
   */
  change(variant) {
    this._resetZoom();
    const colorOption = variant.option1;
    if (this.hasVariantGallery === 'true') {
      this.mediaItems.forEach((element, index) => {
        if (element.getAttribute('data-color') == colorOption) {
          element.classList.add('active');
        } else {
          element.classList.remove('active');
        }
      });

      this.activeSlide = this.el.querySelector('.slider__slide.active');
      if (!this.activeSlide) {
        this._updateModelInfo(null);
      } else {
        this.goToFirstSlide();
        this.initPages();
      }
    } else {
      this.navItems.forEach((element, index) => {
        const { imageVariants } = element.dataset;

        // Bail if array of variants for this image
        // doesn't contain currently selected variant id
        if (!imageVariants.split(',').includes(String(variant.id))) {
          return;
        }

        this.flickity.select(index);
      });
    }
  }

  /**
   * Update Model Info when variant changes
   *
   * @param {Object} variant
   */

  _updateModelInfo(text) {
    const modelInfo = this.el.querySelector('[data-model-info]');
    if (text) {
      modelInfo.innerHTML = text;
      modelInfo.classList.remove('hidden');
    } else {
      modelInfo.classList.add('hidden');
    }
  }

  /**
   * Trigger zoom on selected image
   *
   * @param {Element} selectedImage The image with .is-selected class
   * @public
   */

  _toggleZoom() {
    if (this.imageZoomEnabled === 'true') {
      document.body.classList.toggle('image-zoom--active');
      if (document.body.classList.contains('image-zoom--active')) {
        this._initZoom();
      } else {
        this.drift.destroy();
        this.zoomTrigger.classList.remove('hidden');
      }
    }
  }
  _initZoom() {
    const selectedImage = this.el.querySelector(selectors.selectedImage);
    this.driftZoomArea = selectedImage.querySelector(
      '.product-gallery--image-zoom-container',
    );
    this.drift = new Drift(selectedImage, {
      paneContainer: this.driftZoomArea,
      zoomFactor: 1.5,
      handleTouch: true,
    });
    this.driftZoomArea.addEventListener('click', () => {
      this._resetZoom();
    });
  }

  _resetZoom() {
    if (document.body.classList.contains('image-zoom--active')) {
      this.drift.destroy();
      this.zoomTrigger.classList.remove('hidden');
      document.body.classList.remove('image-zoom--active');
    }
  }
}
